/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import {
  Row,
  Col,
  Spin,
  Form,
  Input,
  InputNumber,
  Button,
  Modal,
  Alert,
  Tag,
  message,
  notification,
  TreeSelect,
} from 'antd';
import { ApartmentOutlined } from '@ant-design/icons';

import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';

import { formatReceived } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import { checkToken } from 'api/utils';
import Select from 'components/Select';
import {
  getCalculoFuentesFinanciamiento,
  getCalculoFuenteFinanciamiento,
  postCalculoFuenteFinanciamiento,
  patchCalculoFuenteFinanciamiento,
  deleteCalculoFuenteFinanciamiento,
  repartirCalculoFuenteFinanciamiento,
} from 'api/catalogos/calculoFuenteFinanciamiento';
import { getPercepcionesParametrosDiversos } from 'api/PercepcionesParametrosDiversos.api';
import currencyFormatter from 'utils/currencyFormatter';
import API from 'utils/api';
import { getClasificadoresObjetoGastoUnion } from 'api/configuraciones/clasificadorObjetoGasto';
import {
  getCOGS, getCuentasContables, getEstadosGlobales, getFuentesDeFinanciamiento, getTipoDeGasto,
} from 'api/catalogos';
import { getTiposDeEmpleadosPresupuestales } from 'api/catalogos/tiposDeEmpleadosPresupuestales';
import { getCATSATOrigenesRecursos } from 'api/nominas/catalogos/CATSATOrigenesRecursos';
import makeTree from 'utils/makeTree';
import makeTreeFromCuentasContables from 'utils/makeTreeFromCuentasContables';
import { getPercepcion, getPercepciones } from 'api/nominas/percepciones/percepciones';

const CalculoFuentesFinanciamiento = ({
  nominaId,
  permission,
  // true cuando la nómina se abrió con "Detalle" (solo consulta): se oculta Editar.
  soloConsulta,
}) => {
  const [form] = Form.useForm();

  const [data, setData] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedData, setSelectedData] = useState({});
  const [loading, setLoading] = useState(false);

  const [open, setOpen] = useState(false);
  const [visibleDelete, setVisibleDelete] = useState(false);

  const [cogs, setCogs] = useState([]);
  const [fuentesFinanciamiento, setFuentesFinanciamiento] = useState([]);
  const [cuentasContables, setCuentasContables] = useState([]);
  const [tiposGasto, setTiposGasto] = useState([]);
  const [origenesSAT, setOrigenesSAT] = useState([]);
  const [tiposEmpleado, setTiposEmpleado] = useState([]);
  const [treeData, setTreeData] = useState([]);
  const [simpleData, setSimpleData] = useState([]);
  const [listaFuentes, setListaFuentes] = useState([]);
  const [estados, setEstados] = useState([]);
  const [percepciones, setPercepciones] = useState([]);

  // Reparto de una Percepción entre sus Fuentes de Financiamiento registradas.
  const [openRepartir, setOpenRepartir] = useState(false);
  const [loadingReparto, setLoadingReparto] = useState(false);
  const [parametrosDiversosDeLaPercepcion, setParametrosDiversosDeLaPercepcion] = useState([]);
  const [repartoImportes, setRepartoImportes] = useState({});

  const fetchData = async () => {
    setLoading(true);
    const _data = await getCalculoFuentesFinanciamiento({
      nomina: nominaId,
    });
    setData(formatReceived(_data));
    setLoading(false);
  };

  const onClickTitle = (e) => {
    const element = e.target;
    if (element) {
      let parent = element.parentElement;
      parent = parent ? parent.parentElement : null;
      if (parent) {
        const clickableSpan = parent.previousSibling;
        if (clickableSpan) {
          clickableSpan.click();
        }
      }
    }
  };

  const renderTitle = (title, bold) => (
    // eslint-disable-next-line
    <span onClick={onClickTitle} className={bold ? 'bold' : ''}>
      {title}
    </span>
  );
  const normalizeTreeCog = (_data) => {
    const newData = {};

    _data.forEach((item) => {
      const clone = { ...item };
      const title = `${clone.clave} - ${clone.concepto}`;
      clone.title = renderTitle(title);
      clone.key = clone.id;
      clone.value = clone.id;

      if (clone.clave_padre_id) {
        const padre = newData[clone.clave_padre_id];
        if (padre) {
          clone.key = `${padre.clave}/${clone.id}`;
          clone.value = `${padre.clave}/${clone.id}`;
          padre.children = padre.children || [];
          padre.children.push(clone);
        }
      }

      //  Evita sobrescribir `seleccionable` con lógica de hijos
      clone.disabled = clone.seleccionable === false;

      if (!newData[clone.id]) {
        newData[clone.id] = clone;
        clone.children = clone.children || undefined;
      }
    });

    const capitulosCog = Object.values(newData).filter((item) => !item.clave_padre_id);
    return capitulosCog;
  };

  const normalizeSimpleData = (_data) => {
    const result = [];
    const processNode = (node) => {
      const copy = { ...node };
      result.push(copy);
      if (copy.children && copy.children.length) {
        copy.children.forEach((child) => {
          processNode(child);
        });
      }
      copy.children = undefined;
    };
    _data.forEach(processNode);
    return result;
  };

  const normalize = (_data = [], level = 0, visited = new Set()) => _data.map((item, index) => {
    const clone = { ...item };
    const uniqueId = clone.id ?? clone.clave;

    if (visited.has(uniqueId)) {
      return null;
    }

    visited.add(uniqueId);

    const children = Array.isArray(clone.children)
      ? normalize(clone.children, level + 1, new Set(visited))
      : [];

    return {
      ...clone,
      key: clone.id,
      value: clone.id,
      title: renderTitle(`${clone.clave} - ${clone.nombre}`, children.length > 0),
      disabled: item.seleccionable === false,
      children: children.length ? children : undefined,
    };
  }).filter(Boolean);

  const formatDataTree = (_data = [], excludeLvl3 = false, currLvl = 1) => _data.map((item) => {
    const clone = { ...item };

    const children = formatDataTree(
      !excludeLvl3 || currLvl !== 2 ? clone.children || [] : [],
      excludeLvl3,
      currLvl + 1,
    );

    const node = {
      key: clone.key || clone.id?.toString(),
      value: clone.value || clone.id?.toString(),
      title: clone.title,
      disabled: !clone.seleccionable,
      children, // mantener siempre
    };

    return node;
  });

  const normalizeCuentasContables = (_data) => {
    if (_data) {
      return _data.map((item) => {
        const clone = { ...item };
        const children = normalizeCuentasContables(clone.children);

        //  Solo es seleccionable si es tipo === 7 (afectable)
        const isAfectable = clone.tipo === 7;

        // Si no tiene hijos, lo evaluamos como hoja
        const isLeaf = !children.length;

        //  Solo habilitamos selección si es hoja y afectable
        const selectable = isLeaf && isAfectable;

        if (!isLeaf) {
          delete clone.children;
        }

        return {
          ...clone,
          title: `${clone.clave_visible || clone.clave} - ${clone.nombre}`,
          value: clone.id,
          key: clone.id,
          children: children.length ? children : undefined,
          disabled: !selectable, //  Solo se puede seleccionar si cumple la condición
        };
      });
    }
    return [];
  };

  const findInTree = (nodes, id) => {
    if (!Array.isArray(nodes)) return null;

    return nodes.reduce((found, node) => {
      if (found) return found;

      if (node.id === id) {
        return node;
      }

      if (node.children) {
        return findInTree(node.children, id) || found;
      }

      return found;
    }, null);
  };

  useEffect(() => {
    let mounted = true;

    const fetchAll = async () => {
      setLoading(true);
      try {
        await checkToken();

        const [
          _data,
          _cogs,
          _origenesSAT,
          _tiposEmpleado,
          _fuentesClasificacion,
          _dataUnion,
          _percepciones,
          _cuentasContables,
        ] = await Promise.all([
          getCalculoFuentesFinanciamiento({ nomina: nominaId }),
          getCOGS(),
          getCATSATOrigenesRecursos(),
          getTiposDeEmpleadosPresupuestales(),
          API.get('contabilidad/clasificaciones-de-fuentes-de-financiamiento/'),
          getClasificadoresObjetoGastoUnion(),
          getPercepciones(),
          getCuentasContables(),
        ]);

        if (!mounted) return;

        const formattedDataUnion = normalizeTreeCog(_dataUnion);
        const _simpleData = normalizeSimpleData(formattedDataUnion);

        setData(formatReceived(_data));
        setCogs(_cogs);
        setOrigenesSAT(_origenesSAT);
        setTiposEmpleado(_tiposEmpleado);
        setSimpleData(_simpleData);

        const fuentesTree = makeTree(_fuentesClasificacion.data);
        const normalized = normalize(fuentesTree);

        setListaFuentes(_fuentesClasificacion.data);
        setFuentesFinanciamiento(formatDataTree(normalized));
        setPercepciones(_percepciones);
        setCuentasContables(
          normalizeCuentasContables(makeTreeFromCuentasContables(_cuentasContables)),
        );
      } catch (err) {
        message.error('Error cargando la información');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    if (nominaId) {
      fetchAll();
    }

    return () => {
      mounted = false;
    };
  }, [nominaId]);

  // El motor de calculo regenera, al recalcular, los registros de CalculoFuenteFinanciamiento
  // que no tengan reparto manual (ver cargar_CalculoFuenteFinanciamiento/calcular_Empleado en
  // nominas_calculos.py) y revalida los que si lo tienen (columna "Requiere Revisión" si su
  // total cambio) -- cualquier alta/edicion/reparto hecho aqui se guarda de inmediato en su
  // propio registro, pero el calculo de la Nomina (Polizas, Presupuesto) no queda actualizado
  // con ese cambio hasta el siguiente "Guardar y Recalcular".
  const avisarRecalcularPendiente = () => {
    notification.info({
      message: 'Cambios sin reflejar en el cálculo',
      description: 'Este cambio en Fuentes de Financiamiento ya se guardó. Presiona "Guardar y Recalcular" para que el cálculo de la Nómina (Pólizas, Presupuesto) quede actualizado.',
      placement: 'topRight',
      duration: 0,
    });
  };

  const onCancel = () => {
    setOpen(false);
    setSelectedRowKeys([]);
    setSelectedData({});
    form.resetFields();
  };

  const onClickAdd = () => {
    form.resetFields();
    setOpen(true);
  };

  const onClickEdit = async () => {
    const [key] = selectedRowKeys;
    if (!key) return;

    setLoading(true);
    const item = await getCalculoFuenteFinanciamiento(key);
    if (item) {
      form.setFieldsValue(formatReceived(item));
      setOpen(true);
    }
    setLoading(false);
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = {
        ...form.getFieldsValue(),
        nomina: nominaId,
      };

      const [key] = selectedRowKeys;
      const saved = key
        ? await patchCalculoFuenteFinanciamiento(key, values)
        : await postCalculoFuenteFinanciamiento(values);

      if (saved) {
        onCancel();
        await fetchData();
        avisarRecalcularPendiente();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading, [form]);
    }
  };

  const deleteItem = async () => {
    const [key] = selectedRowKeys;
    if (!key) return;

    setLoading(true);
    const deleted = await deleteCalculoFuenteFinanciamiento(key);
    if (deleted) {
      setVisibleDelete(false);
      onCancel();
      await fetchData();
      avisarRecalcularPendiente();
    }
    setLoading(false);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedData(record);
  };

  // Fuentes de Financiamiento registradas (catálogo) para la Percepción + Tipo Empleado
  // Presupuestal de la fila seleccionada -- determina si se puede repartir (más de 1 registrada)
  // y arma las filas del modal de Reparto.
  useEffect(() => {
    let mounted = true;

    const fetchParametrosDiversos = async () => {
      if (!selectedData?.percepcion || !selectedData?.tipo_empleado_presupuestal) {
        setParametrosDiversosDeLaPercepcion([]);
        return;
      }
      const _parametrosDiversos = await getPercepcionesParametrosDiversos({
        percepcion: selectedData.percepcion,
        tipo_empleado_presupuestal: selectedData.tipo_empleado_presupuestal,
      });
      if (mounted) {
        setParametrosDiversosDeLaPercepcion(formatReceived(_parametrosDiversos) || []);
      }
    };

    fetchParametrosDiversos();

    return () => {
      mounted = false;
    };
  }, [selectedData?.percepcion, selectedData?.tipo_empleado_presupuestal]);

  const filasDeLaMismaPercepcion = () => data.filter((row) => (
    row.percepcion === selectedData.percepcion
    && row.tipo_empleado_presupuestal === selectedData.tipo_empleado_presupuestal
  ));

  const abrirRepartir = () => {
    const filas = filasDeLaMismaPercepcion();
    const totalActual = filas.reduce((acc, row) => acc + Number(row.importe || 0), 0);
    // Evita que una misma fila ya existente (p. ej. la única fila sin repartir) se le asigne
    // a más de un candidato de PercepcionParametroDiverso que comparta la misma Fuente de
    // Financiamiento pero con distinto COG/Cuenta Contable.
    const filasYaAsignadas = new Set();
    let yaAsignoImporteAUnPrincipal = false;

    const importesIniciales = {};
    parametrosDiversosDeLaPercepcion.forEach((pd) => {
      const filaExistente = filas.find((row) => !filasYaAsignadas.has(row.id)
        && row.fuente_financiamiento === pd.fuente_financiamiento
        && row.cog === pd.cog
        && row.cuenta_contable === pd.cuenta_contable);
      if (filaExistente) {
        filasYaAsignadas.add(filaExistente.id);
        importesIniciales[pd.id] = Number(filaExistente.importe || 0);
      } else if (pd.es_fuente_de_financiamiento_principal && !yaAsignoImporteAUnPrincipal) {
        importesIniciales[pd.id] = totalActual;
        yaAsignoImporteAUnPrincipal = true;
      } else {
        importesIniciales[pd.id] = 0;
      }
    });

    setRepartoImportes(importesIniciales);
    setOpenRepartir(true);
  };

  const onChangeRepartoImporte = (parametroDiversoId, value) => {
    setRepartoImportes((prev) => ({ ...prev, [parametroDiversoId]: value || 0 }));
  };

  const totalObjetivoReparto = () => filasDeLaMismaPercepcion()
    .reduce((acc, row) => acc + Number(row.importe || 0), 0);

  const totalCapturadoReparto = () => Object.values(repartoImportes)
    .reduce((acc, val) => acc + Number(val || 0), 0);

  const onGuardarReparto = async () => {
    setLoadingReparto(true);
    const payload = {
      nomina: nominaId,
      percepcion: selectedData.percepcion,
      tipo_empleado_presupuestal: selectedData.tipo_empleado_presupuestal,
      repartos: Object.entries(repartoImportes).map(([parametroDiversoId, importe]) => ({
        parametro_diverso_id: Number(parametroDiversoId),
        importe,
      })),
    };
    const saved = await repartirCalculoFuenteFinanciamiento(payload);
    if (saved) {
      setOpenRepartir(false);
      setSelectedRowKeys([]);
      setSelectedData({});
      await fetchData();
      message.success('Reparto guardado. Revisa el tab "Pólizas" para consultar la Póliza actualizada.');
      avisarRecalcularPendiente();
    }
    setLoadingReparto(false);
  };

  const onUpdateRow = async (id, payload) => {
    try {
      setLoading(true);

      const updated = await patchCalculoFuenteFinanciamiento(id, payload);

      if (updated) {
        setData((prev) => prev.map((row) => (row.id === id ? { ...row, ...updated } : row)));
      }

      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const columns = [
    {
      titleText: 'Percepción',
      dataIndex: 'percepcion',
      key: 'percepcion',
      width: 220,
      render: (val) => {
        if (!percepciones?.length) return '';
        const p = percepciones.find((x) => x.id === val);
        return p ? `${p.clave} - ${p.descripcion}` : '';
      },
    },
    {
      titleText: 'Clasificador por Objeto del Gasto (COG)',
      dataIndex: 'cog_descripcion',
      key: 'cog',
      width: 260,

    },
    {
      titleText: 'Tipo Empleado Presupuestal',
      dataIndex: 'tipo_empleado_presupuestal',
      key: 'tipo_empleado_presupuestal',
      width: 220,
      render: (val) => tiposEmpleado.find((t) => t.id === val)?.descripcion || '',
    },

    {
      titleText: 'Fuente de Financiamiento',
      dataIndex: 'fuente_financiamiento',
      key: 'fuente_financiamiento',
      width: 260,
      render: (val) => {
        const fuente = listaFuentes.find((f) => f.id === val);
        return fuente ? `${fuente.clave} - ${fuente.nombre}` : val;
      },
    },

    {
      titleText: 'Origen del Recurso del SAT',
      dataIndex: 'catsat_origen_recurso',
      key: 'catsat_origen_recurso',
      width: 220,
      render: (val) => origenesSAT.find((o) => o.id === val)?.descripcion || val,
    },

    {
      titleText: 'Cuenta Contable',
      dataIndex: 'cuenta_contable',
      key: 'cuenta_contable',
      width: 320,
      render: (val) => {
        if (!val) return '';
        const cuenta = findInTree(cuentasContables, val);
        return cuenta ? `${cuenta.clave_visible || cuenta.clave} - ${cuenta.nombre}` : val;
      },
    },

    {
      titleText: 'Importe',
      dataIndex: 'importe',
      key: 'importe',
      width: 140,
      align: 'right',
      render: (val) => currencyFormatter(val),
    },
    {
      titleText: 'Forma de Pago',
      dataIndex: 'forma_pago_descripcion',
      key: 'forma_pago',
      width: 200,
    },
    {
      titleText: 'Requiere Revisión',
      dataIndex: 'requiere_revision',
      key: 'requiere_revision',
      width: 160,
      render: (val) => (val ? <Tag color="warning">Requiere Revisión</Tag> : null),
    },
  ];

  const hayFilasQueRequierenRevision = data.some((row) => row.requiere_revision);

  // Autosuma del Importe sobre TODos los registros (no solo los de la página actual).
  const totalImporte = data.reduce((suma, actual) => suma + (Number(actual.importe) || 0), 0);

  const summaryCells = [
    {
      label: 'Total',
      labelColSpan: 6,
      labelAlign: 'right',
      value: totalImporte,
      valueAlign: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      value: '',
      valueColSpan: 2,
    },
  ];

  // Ordenados por Percepción (clave) para que las distintas Fuentes de Financiamiento de una
  // misma Percepción (p. ej. por Tipo Empleado Presupuestal) queden agrupadas una tras otra.
  const dataOrdenadaPorPercepcion = [...data].sort((a, b) => {
    const claveA = percepciones.find((p) => p.id === a.percepcion)?.clave || '';
    const claveB = percepciones.find((p) => p.id === b.percepcion)?.clave || '';
    return claveA.localeCompare(claveB, 'es', { numeric: true }) || (a.id - b.id);
  });

  return (
    <Row className="container">
      <Spin spinning={loading}>
        {hayFilasQueRequierenRevision && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="Hay Fuentes de Financiamiento repartidas manualmente cuyo total cambió al recalcular la Nómina."
            description='Revísalas (columna "Requiere Revisión") antes de confiar en la Póliza.'
          />
        )}
        <Table
          data={dataOrdenadaPorPercepcion}
          cols={columns}
          summaryCells={summaryCells}
          rowSelection={{
            type: 'radio',
            selectedRowKeys,
          }}
          handleOnRowClick={handleOnRowClick}
          controls={soloConsulta ? {} : {
            // onClickAdd,
            onClickEdit,
            // onClickDelete: () => setVisibleDelete(true),
          }}
          customActions={soloConsulta ? [] : [
            {
              onClick: abrirRepartir,
              permission: { model: 'calculofuentefinanciamiento', action: 'change' },
              icon: <ApartmentOutlined />,
              text: 'Fuentes de Financiamiento',
              disabledBySelection: true,
              disabled: parametrosDiversosDeLaPercepcion.length < 2,
              tooltip: parametrosDiversosDeLaPercepcion.length < 2
                ? 'Esta Percepción solo tiene una Fuente de Financiamiento registrada; no hay nada que repartir.'
                : undefined,
              className: 'primary',
            },
          ]}
          permission={permission}
        />

        <Modal
          open={open}
          footer={null}
          closable={false}
          forceRender
          onCancel={onCancel}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Fuente de Financiamiento`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
            />
          )}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
          >
            {/* Fuente de Financiamiento */}
            <Col span={24}>
              <Form.Item
                name="fuente_financiamiento"
                label="Fuente de Financiamiento"
                rules={[{ required: true, message: 'Seleccione una fuente de financiamiento' }]}
              >
                <TreeSelect
                  showSearch
                  treeNodeFilterProp="title"
                  style={{ width: '100%' }}
                  dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                  treeData={fuentesFinanciamiento}
                  allowClear
                />
              </Form.Item>
            </Col>

            {/* Origen del Recurso SAT */}
            <Col span={24}>
              <Form.Item
                name="catsat_origen_recurso"
                label="Origen del Recurso SAT"
                rules={[{ required: true, message: 'Seleccione un origen del recurso' }]}
              >
                <Select
                  dataSource={origenesSAT}
                  keyProp="id"
                  labelProp="descripcion"
                  placeholder="Seleccione un origen del recurso SAT"
                />
              </Form.Item>
            </Col>

            {/* Cuenta Contable */}
            <Col span={24}>
              <Form.Item
                name="cuenta_contable"
                label="Cuenta Contable"
              >
                <TreeSelect
                  showSearch
                  treeNodeFilterProp="title"
                  style={{ width: '100%' }}
                  dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                  treeData={cuentasContables}
                  allowClear
                />
              </Form.Item>
            </Col>

            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          open={openRepartir}
          footer={null}
          closable={false}
          forceRender
          onCancel={() => setOpenRepartir(false)}
          title={(
            <FormSubmitControls
              label="Repartir Fuente de Financiamiento"
              onFinish={onGuardarReparto}
              onCancel={() => setOpenRepartir(false)}
              loading={loadingReparto}
              disableOnFinish={Math.abs(totalCapturadoReparto() - totalObjetivoReparto()) > 0.01}
            />
          )}
          width={720}
        >
          <Spin spinning={loadingReparto}>
            <p>
              {percepciones.find((p) => p.id === selectedData.percepcion)?.clave}
              {' - '}
              {percepciones.find((p) => p.id === selectedData.percepcion)?.descripcion}
            </p>
            {parametrosDiversosDeLaPercepcion.map((pd) => {
              const fuente = listaFuentes.find((f) => f.id === pd.fuente_financiamiento);
              const cuenta = pd.cuenta_contable
                ? findInTree(cuentasContables, pd.cuenta_contable) : null;
              return (
                <Row key={pd.id} gutter={10} align="middle" style={{ marginBottom: 12 }}>
                  <Col span={14}>
                    <div>
                      {fuente ? `${fuente.clave} - ${fuente.nombre}` : ''}
                      {pd.es_fuente_de_financiamiento_principal && (
                        <Tag color="blue" style={{ marginLeft: 8 }}>Principal</Tag>
                      )}
                    </div>
                    <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12 }}>
                      {cuenta ? `${cuenta.clave_visible || cuenta.clave} - ${cuenta.nombre}` : 'Sin Cuenta Contable'}
                    </div>
                  </Col>
                  <Col span={10}>
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      precision={2}
                      value={repartoImportes[pd.id]}
                      onChange={(value) => onChangeRepartoImporte(pd.id, value)}
                      formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                    />
                  </Col>
                </Row>
              );
            })}
            <Row justify="space-between" style={{ marginTop: 16, fontWeight: 'bold' }}>
              <Col>
                Total capturado:
                {' '}
                {currencyFormatter(totalCapturadoReparto())}
              </Col>
              <Col>
                Total de la Percepción:
                {' '}
                {currencyFormatter(totalObjetivoReparto())}
              </Col>
            </Row>
          </Spin>
        </Modal>

        <ModalDelete
          open={visibleDelete}
          loading={loading}
          onCancel={() => setVisibleDelete(false)}
          onDelete={deleteItem}
          content="Fuente de Financiamiento"
        />
      </Spin>
    </Row>
  );
};

export default CalculoFuentesFinanciamiento;
